import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, FormControlLabel, Switch, Typography } from '@mui/material';
import { agentVoiceApi, getAgentVoiceCapabilities } from '../../services/api';
import { agentOwner } from './agentDelivery';
import { LiveVoiceHost } from './liveVoiceHost';

const phaseLabel = (view) => {
  if (view.renewing) return view.microphoneMuted ? 'Microphone muted · Reconnecting' : 'Reconnecting';
  if (view.phase === 'connecting') return 'Connecting';
  if (view.phase === 'ended') return 'Voice ended';
  const activity = view.audioPhase === 'speaking' ? 'Speaking'
    : view.audioPhase === 'audio_starting' ? 'Starting audio' : view.thinking ? 'Thinking' : null;
  if (view.phase === 'muted') return ['Microphone muted', activity].filter(Boolean).join(' · ');
  return activity || 'Listening';
};

/** Start is the only path that acquires media; recovery never resumes capture. */
export default function LiveVoiceControl({ active, owner, page, threadId, disabled, getContext, getAttachments,
  onThread, onSnapshot, onAccepted, onState, controlRef }) {
  const props = useRef();
  props.current = { threadId, disabled, getContext, getAttachments, onThread, onSnapshot, onAccepted, onState };
  const hostRef = useRef(null);
  const [available, setAvailable] = useState(false);
  const [saved, setSaved] = useState(null);
  const [view, setView] = useState({ phase: 'idle' });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [spokenReplies, setSpokenReplies] = useState(true);

  useEffect(() => {
    if (!active || !owner) return undefined;
    let alive = true;
    const current = () => alive && agentOwner() === owner;
    const host = new LiveVoiceHost({ owner, currentOwner: agentOwner, page, api: agentVoiceApi(owner),
      getContext: () => props.current.getContext(), getAttachments: () => props.current.getAttachments(),
      onThread: (id) => current() && props.current.onThread(id),
      onSnapshot: (snapshot) => { if (current()) props.current.onSnapshot(snapshot); },
      onAccepted: (receipt, ids) => { if (current()) props.current.onAccepted(receipt, ids); },
      onState: (state) => {
        if (!current()) return;
        setView(state); setSaved(state.journal);
        props.current.onState({ ...state, blocked: state.captureActive || state.phase === 'connecting' || state.renewing || state.unconfirmedInputs > 0,
          hasSavedCall: Boolean(state.journal) });
      },
    });
    hostRef.current = host;
    if (controlRef) controlRef.current = host;
    setView({ phase: 'idle' }); setError(null); setBusy(false); setSeconds(0); setAvailable(false);
    try {
      const journal = host.intake.read(); setSaved(journal);
      props.current.onState({ blocked: Boolean(journal?.inputs.some((item) => !item.receipt) || journal?.captureDrafts?.length), hasSavedCall: Boolean(journal) });
    } catch (failure) { setError(failure.message); props.current.onState({ blocked: true, hasSavedCall: true }); }
    Promise.resolve().then(() => getAgentVoiceCapabilities()).then((reply) => {
      if (current() && reply.data.enabled === true) setAvailable(true);
    }).catch(() => { /* Ordinary text remains available if capabilities are offline. */ });
    return () => {
      alive = false; host.dispose();
      if (controlRef?.current === host) controlRef.current = null;
      if (hostRef.current === host) { hostRef.current = null; props.current.onState({ blocked: false, hasSavedCall: false }); }
    };
  }, [active, owner, page, controlRef]);

  const capture = view.captureActive || view.phase === 'connecting' || view.renewing;
  const microphoneMuted = view.microphoneMuted ?? view.phase === 'muted';
  useEffect(() => {
    if (!capture) return undefined;
    const started = view.startedAt ?? Date.now(); setSeconds(Math.floor((Date.now()-started)/1000));
    const timer = setInterval(() => setSeconds(Math.floor((Date.now()-started)/1000)), 1000);
    return () => clearInterval(timer);
  }, [capture, view.startedAt]);

  const perform = async (action) => {
    const host = hostRef.current;
    if (!host || agentOwner() !== owner) return;
    setBusy(true); setError(null);
    try {
      await action(host);
      if (hostRef.current === host && agentOwner() === owner) {
        const journal = host.intake.read(); setSaved(journal);
        if (!host.run) props.current.onState({ blocked: Boolean(journal?.inputs.some((item) => !item.receipt) || journal?.captureDrafts?.length), hasSavedCall: Boolean(journal) });
      }
    } catch (failure) {
      if (hostRef.current === host && agentOwner() === owner) setError(failure.response?.data?.error || failure.message || 'Voice could not connect. Text chat is still available.');
    } finally { if (hostRef.current === host) setBusy(false); }
  };

  if (!active || (!available && !saved && !error && !view.pendingInputs?.length)) return null;
  const drafts = [
    ...(saved?.inputs || []).filter((item) => item.notAccepted && !item.receipt).map((item) => ({ id: item.itemId, text: item.payload.message, kind: 'input' })),
    ...(saved?.captureDrafts || []).map((item) => ({ id: item.itemId, text: item.transcript, kind: 'speech' })),
    ...(view.pendingInputs || []).filter((item) => item.transcript).map((item) => ({ id: item.itemId, text: item.transcript, kind: 'unsaved' })),
  ];
  return <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }} aria-label="Live voice controls">
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      {capture ? <>
        <Typography variant="body2" role="status">AI voice · {phaseLabel(view)} · {Math.floor(seconds/60)}:{String(seconds%60).padStart(2,'0')}</Typography>
        <Button size="small" onClick={() => hostRef.current?.mute(!microphoneMuted)}>{microphoneMuted ? 'Unmute' : 'Mute'}</Button>
        {['audio_starting', 'speaking'].includes(view.audioPhase) && <Button size="small" onClick={() => hostRef.current?.stopSpeaking()}>Stop speaking</Button>}
        <Button size="small" color="error" onClick={() => perform((host) => host.end())}>End voice</Button>
      </> : saved ? <Button size="small" disabled={busy} onClick={() => perform((host) => host.reconcile())}>Reconcile previous voice call</Button>
        : <Button size="small" disabled={!available || disabled || busy || drafts.length > 0} onClick={() => perform((host) => {
          if (props.current.disabled) return;
          host.setSpokenReplies(spokenReplies); return host.start(props.current.threadId);
        })}>Start live voice</Button>}
      {(available || capture) && <FormControlLabel sx={{ mr: 0 }} control={<Switch size="small" checked={spokenReplies} onChange={(_, value) => {
        setSpokenReplies(value); hostRef.current?.setSpokenReplies(value);
      }} />} label={<Typography variant="caption">Spoken replies</Typography>} />}
    </Box>
    {capture && <Typography variant="caption">Speak naturally. You can interrupt. Closing or leaving this page ends microphone capture.</Typography>}
    {view.detail?.startsWith('screen_awake_') && <Alert severity="warning">This browser could not keep the screen awake. Keep the page visible during your call.</Alert>}
    {view.detail === 'backgrounded' && <Alert severity="info">Voice ended when the page left the foreground. Press Start to speak again.</Alert>}
    {view.audioPhase === 'audio_unavailable' && <Alert severity="info">Spoken output is unavailable for this call. Your answer and any saved action remain in the chat.</Alert>}
    {(view.repeatSpeech || view.detail === 'speech_not_transcribed') && <Alert severity="warning">That speech was not transcribed. Please repeat it or type it.</Alert>}
    {view.connectionRecovered && !view.repeatSpeech && capture && !view.renewing && view.phase !== 'connecting'
      && <Alert severity="info">Voice reconnected. Accepted requests remain saved. Check the chat before repeating your last request.</Alert>}
    {view.connectionLost && <Alert severity="info">Reconnecting to the conversation. Accepted requests remain saved.</Alert>}
    {view.detail === 'voice_reconnect_stopped' && <Alert severity="warning">Live voice stopped after repeated connection failures. Press Start to try again.</Alert>}
    {error && <Alert severity="warning">{error}</Alert>}
    {drafts.map((draft) => <Alert severity="info" key={draft.kind+draft.id} sx={{ mt: 1 }}>
      {draft.kind === 'unsaved' ? 'This unsent speech could not be saved on this device. Copy it before closing: ' : 'This speech was not submitted: '}{draft.text}
      <Box><Button size="small" onClick={() => navigator.clipboard.writeText(draft.text).catch(() => setError('Copy was unavailable. Your speech is still saved.'))}>Copy text</Button>
        <Button size="small" disabled={busy} onClick={() => perform(async (host) => {
          if (draft.kind === 'input') host.intake.releaseUnaccepted(draft.id);
          else if (draft.kind === 'speech') host.intake.releaseSpeechDraft(draft.id);
          else host.discardUnsavedSpeech(draft.id);
          await host.reconcile();
        })}>Discard unsent speech</Button></Box>
    </Alert>)}
  </Box>;
}
